import { styled } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { DatePickerProps, DatePickerSlots } from '@mui/x-date-pickers/DatePicker';
import { usePickerContext } from '@mui/x-date-pickers/hooks';
import { monthCalendarClasses } from '@mui/x-date-pickers/MonthCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { PickersLayout } from '@mui/x-date-pickers/PickersLayout';

import DatePicker from 'components/date-picker';
import { useChartMode } from 'components/charts/hooks/use-chart-mode';
import { useGrowthState } from '../context/growth-context';
import { snapToPeriod } from '../utils/snap-to-period';

const DATE_FORMAT = 'YYYY-MM-DD';

type DateField = 'from' | 'to';
type PickersLayoutProps = React.ComponentProps<NonNullable<DatePickerSlots['layout']>>;

const StyledWeekDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'isWeekSelected',
})<{ isWeekSelected: boolean }>(({ theme, day, isWeekSelected }) => ({
  ...(isWeekSelected && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
    },
    ...(day.isoWeekday() === 1 && {
      borderTopLeftRadius: '50%',
      borderBottomLeftRadius: '50%',
    }),
    ...(day.isoWeekday() === 7 && {
      borderTopRightRadius: '50%',
      borderBottomRightRadius: '50%',
    }),
  }),
}));

// Highlights the whole ISO week of the picker's value instead of a single day.
const WeekPickerDay: React.FC<PickersDayProps> = (props) => {
  const { value } = usePickerContext();
  const isWeekSelected = value != null && props.day.isSame(value, 'isoWeek');

  return <StyledWeekDay {...props} selected={false} aria-selected={isWeekSelected} disableMargin isWeekSelected={isWeekSelected} />;
};

// The month view renders 3 months per row, so a quarter is one row. The month buttons carry no month number,
// so the row is picked by position. shortcut: it highlights the same row in every year the picker pages to.
// Like the week band: no gaps between the row's months, and only the outer ends rounded (18 = half the button height).
// PickersLayout is generic over the picker value; styled() needs it fixed to the single-date value.
const StyledQuarterLayout = styled(PickersLayout as React.FC<PickersLayoutProps>, {
  shouldForwardProp: (prop) => prop !== 'selectedQuarter',
})<{ selectedQuarter: number }>(({ theme, selectedQuarter }) => {
  const month = (offset: number) => `& .${monthCalendarClasses.button}:nth-of-type(${(selectedQuarter - 1) * 3 + offset})`;

  return {
    [`& .${monthCalendarClasses.root}`]: { columnGap: 0, justifyContent: 'center' },
    [`& .${monthCalendarClasses.button}`]: { width: '30%' },
    [[1, 2, 3].map(month).join(', ')]: {
      borderRadius: 0,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    [month(1)]: { borderTopLeftRadius: 18, borderBottomLeftRadius: 18 },
    [month(3)]: { borderTopRightRadius: 18, borderBottomRightRadius: 18 },
  };
});

const QuarterPickerLayout: React.FC<PickersLayoutProps> = (props) => {
  const { value } = usePickerContext();

  return <StyledQuarterLayout {...props} selectedQuarter={value?.quarter() ?? 0} />;
};

const GrowthDateRangePicker: React.FC = () => {
  const intl = useIntl();
  const { filters, setFilters } = useGrowthState();
  // The shared inputs call a light surface "dark" (dark text on a white field).
  const darkText = useChartMode() === 'light';
  // The open picker renders from its value prop, so year/month steps live in a draft and only an accept refetches.
  const [draft, setDraft] = useState<{ field: DateField; date: Dayjs } | null>(null);
  const { grouping, dateRange } = filters;
  const pickMonths = grouping === 'month' || grouping === 'quarter';
  const views: DatePickerProps['views'] = pickMonths ? ['year', 'month'] : ['year', 'month', 'day'];
  const openTo: DatePickerProps['openTo'] = pickMonths ? 'month' : 'day';

  const pickerValue = (field: DateField) => (draft?.field === field ? draft.date : dateRange[field]);
  const acceptDate = (field: DateField, date: Dayjs | null, validationError: string | null) => {
    setDraft(null);
    if (date?.isValid() && !validationError) {
      setFilters({ ...filters, dateRange: { ...dateRange, [field]: snapToPeriod(date, grouping, field) } });
    }
  };

  const sharedProps = {
    dark: darkText,
    format: pickMonths ? 'MMM YYYY' : DATE_FORMAT,
    views,
    openTo,
    showDaysOutsideCurrentMonth: grouping === 'week',
    slots: { ...(grouping === 'week' && { day: WeekPickerDay }), ...(grouping === 'quarter' && { layout: QuarterPickerLayout }) },
    onClose: () => setDraft(null),
  };

  return (
    <>
      <DatePicker
        {...sharedProps}
        label={intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.filters.fromDate' })}
        value={pickerValue('from')}
        maxDate={dateRange.to}
        onChange={(date) => setDraft({ field: 'from', date })}
        onAccept={(date, { validationError }) => acceptDate('from', date, validationError)}
      />
      <DatePicker
        {...sharedProps}
        label={intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.filters.toDate' })}
        value={pickerValue('to')}
        minDate={dateRange.from}
        // End of the current period, not today, so a snapped To never lands past the limit.
        maxDate={snapToPeriod(dayjs(), grouping, 'to')}
        onChange={(date) => setDraft({ field: 'to', date })}
        onAccept={(date, { validationError }) => acceptDate('to', date, validationError)}
      />
    </>
  );
};

export default GrowthDateRangePicker;
